import axios from "axios";
import { isEmpty, omit, omitBy, isNil } from "lodash";
import useSession from "Hooks/session.hook";
import errorHandler from "Utils/error-handler";
import { getAccessToken } from "Helpers/session";
import { DefaultFilter } from "Types/common.types";
import { string } from "yup";
import normalize from "Utils/normalizer";

const url = `${process.env.API_URL}/${process.env.API_VER}`;

interface Filters extends DefaultFilter {
  orFields?: string;
  active?: boolean;
  ancestorOrgIDs?: any;
}

/*
    CTRL-F the following to find their APIs (ordered as so)
    - RACK
    - BOX
    - TROLLEY
    - VEHICLE
    - SERVICE POINT
    - SERVICE POINT TYPE
    - LINE
    - ROUTING

    Each item in the above list has a create, list, fetch
    and update API (Rack have one extra: Slot Update).
*/

const getGlobalStateOrgID = () => {
  const globalFilterLocal = localStorage.getItem("globalFilter");
  let savedGlobalFilter = JSON.parse(globalFilterLocal);
  return savedGlobalFilter?.selectedOrgIDs;
};

// ---- RACK  ---- //

export const createRack = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createRack(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchRacks = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.RACK;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchRacks(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchRack = async (rackID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          rackID: rackID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.RACK;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchRack(rackID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateRack = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateRack(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateSlot = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/slot/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateSlot(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listSlotsData = async (filters, rackLocation?: string) => {
  const accessToken = getAccessToken();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/rack/slot_data/list`,
      data: { rackLocationID: string, ...filters },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return normalize.response.slots_data(response.data.racks);
  } catch {}
};

// ---- BOX  ---- //

export const createBox = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/box/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omit(inputData, "noOfUnits"),
        quantity: inputData.noOfUnits,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createBox(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchBoxes = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/box/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.BOX;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchBoxes(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchBox = async (boxID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/box/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          boxID: boxID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.BOX;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchBox(boxID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateBox = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/box/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateBox(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- TROLLEY  ---- //

export const createTrolley = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/trolley/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omit(inputData, "noOfUnits"),
        quantity: inputData.noOfUnits,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createTrolley(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchTrolleys = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/trolley/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.TROLLEY;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrolleys(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchTrolley = async (trolleyID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/trolley/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          trolleyID: trolleyID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.TROLLEY;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchTrolley(trolleyID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateTrolley = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/trolley/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateTrolley(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- VEHICLE  ---- //

export const createVehicle = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createVehicle(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listVehicles = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();
  const ancestorOrgIDs = getGlobalStateOrgID();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: ancestorOrgIDs ? ancestorOrgIDs[0] : org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.VEHICLE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listVehicles(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchVehicle = async (vehicleID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          vehicleID: vehicleID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.VEHICLE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchVehicle(vehicleID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateVehicle = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateVehicle(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- VEHICLE TYPES ---- //

export const createVehicleType = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle_type/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createVehicleType(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listVehicleTypes = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();
  const ancestorOrgIDs = getGlobalStateOrgID();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle_type/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: ancestorOrgIDs ? ancestorOrgIDs[0] : org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.VEHICLE_TYPE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listVehicleTypes(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchVehicleType = async (vehicleTypeID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle_type/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          vehicleTypeID: vehicleTypeID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.VEHICLE_TYPE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchVehicleType(vehicleTypeID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateVehicleType = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/vehicle_type/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateVehicleType(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- SERVICE POINT  ---- //

export const createServicePoint = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePoint/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createServicePoint(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listServicePoints = async (
  filters?: any,
  offset?: number,
  limit?: number,
  sortBy?: string,
) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePoint/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            sortBy: sortBy,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.SERVICE_POINT;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listServicePoints(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchServicePoint = async (pointID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePoint/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          servicePointID: pointID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.SERVICE_POINT;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchServicePoint(pointID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateServicePoint = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePoint/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateServicePoint(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- SERVICE POINT TYPE ---- //

export const createServicePointType = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePointType/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createServicePointType(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchServicePointTypes = async (
  filters?: Filters,
  offset?: number,
  limit?: number,
) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  const isRoleCustomer = org.orgType == "CUSTOMER" ? true : false;

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePointType/list`,
      data: {
        admin: {
          orgID: isRoleCustomer ? org.parentID : org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: isRoleCustomer ? org.parentID : org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.SERVICE_POINT_TYPE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchServicePointTypes(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchServicePointType = async (pointTypeID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePointType/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          servicePointTypeID: pointTypeID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.SERVICE_POINT_TYPE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchServicePointType(pointTypeID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateServicePointType = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/servicePointType/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateServicePointType(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- LINE ---- //

export const createLine = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/line/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await createLine(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listLines = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/line/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.LINE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listLines(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchLine = async (lineID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/line/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          lineID: lineID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.LINE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchLine(lineID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateLine = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/line/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateLine(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

// ---- ROUTING  ---- //
export const createAlgoTiming = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/routing_algo/create`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: { ...inputData },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await createAlgoTiming(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const listAlgoTiming = async (filters?: Filters, offset?: number, limit?: number) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/routing_algo/list`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: omitBy(
          {
            orgID: org._id,
            filter: filters,
            pagination: omitBy(
              {
                offset: offset,
                limit: limit,
              },
              isNil,
            ),
          },
          isEmpty,
        ),
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.ROUTING_ALGORITHM;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await listAlgoTiming(filters);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const updateAlgoTiming = async (inputData: any) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/routing_algo/update`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: inputData,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response;
  } catch (err: any) {
    const { error } = err.response?.data || {};
    console.log(err.response);

    if (!isEmpty(error)) {
      const callback = async () => await updateAlgoTiming(inputData);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};

export const fetchAlgoTiming = async (routingAlgoID: string) => {
  const accessToken = getAccessToken();
  const { org, profile } = useSession();

  try {
    const response = await axios({
      method: "POST",
      url: `${url}/delivery_setting/line/fetch`,
      data: {
        admin: {
          orgID: org._id,
          profileID: profile._id,
        },
        data: {
          routingAlgoID: routingAlgoID,
        },
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data.LINE;
  } catch (err: any) {
    const { error } = err.response?.data || {};

    if (!isEmpty(error)) {
      const callback = async () => await fetchAlgoTiming(routingAlgoID);
      const { value, code, message, stack } = await errorHandler.response(error, { callback });
      return { error: { value, code, message, stack } };
    } else {
      console.log(err.message);
      return { error: { message: err.message } };
    }
  }
};
